import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { appLoggerPort } from "@/adapters/platformPorts";
import { adminIdentityPort } from "@/adapters/adminIdentityPort";
import type { AdminIdentityCurrentUser as User } from "@tihany/module-admin-contracts";
import type { AuthContext as PlatformAuthContext, IdentityUser } from "@tihany/platform-contracts";
import {
    INTERNAL_POLICY_KEY,
    INTERNAL_POLICY_VERSION,
    resolveInternalPolicyLocale,
} from "@/lib/internalPolicy";
import { getInitialUiLanguage } from "@/lib/languages";

const clearSupabaseAuthStorage = () => {
    try {
        Object.keys(localStorage)
            .filter((key) => key.startsWith("sb-"))
            .forEach((key) => localStorage.removeItem(key));
    } catch (error) {
        appLoggerPort.warn("Failed to clear Supabase auth storage.", { error });
    }
};

type AuthContextValue = {
    user: User | null;
    isAdmin: boolean;
    loading: boolean;
    internalPolicyAccepted: boolean;
    internalPolicyAcceptedAt: string | null;
    internalPolicyLoading: boolean;
    signOut: () => Promise<void>;
    acceptInternalPolicy: (locale?: string) => Promise<void>;
    refreshInternalPolicy: () => Promise<void>;
    platformAuthContext: PlatformAuthContext;
};

const toPlatformIdentityUser = (user: User | null): IdentityUser | null => {
    if (!user) return null;
    return {
        id: user.id,
        email: user.email ?? null,
        displayName: null,
    };
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [internalPolicyAcceptedAt, setInternalPolicyAcceptedAt] = useState<string | null>(null);
    const [internalPolicyLoading, setInternalPolicyLoading] = useState(true);

    const loadInternalPolicyAcceptance = useCallback(async (userId: string) => {
        setInternalPolicyLoading(true);
        try {
            const acceptance = await adminIdentityPort.getLegalAcceptance(INTERNAL_POLICY_KEY, INTERNAL_POLICY_VERSION);
            if (!acceptance || acceptance.user_id !== userId) {
                setInternalPolicyAcceptedAt(null);
                return;
            }
            setInternalPolicyAcceptedAt(acceptance.accepted_at);
        } catch (error) {
            appLoggerPort.warn("Failed to load internal policy acceptance.", { error, userId });
            setInternalPolicyAcceptedAt(null);
        } finally {
            setInternalPolicyLoading(false);
        }
    }, []);

    const refreshInternalPolicy = useCallback(async () => {
        if (!user?.id) {
            setInternalPolicyAcceptedAt(null);
            setInternalPolicyLoading(false);
            return;
        }
        await loadInternalPolicyAcceptance(user.id);
    }, [loadInternalPolicyAcceptance, user?.id]);

    const acceptInternalPolicy = useCallback(async (locale?: string) => {
        if (!user?.id) {
            throw new Error("No signed-in user");
        }

        const resolvedLocale = resolveInternalPolicyLocale(locale ?? getInitialUiLanguage("hu"));

        setInternalPolicyLoading(true);
        try {
            const acceptance = await adminIdentityPort.acceptLegalPolicy({
                policyKey: INTERNAL_POLICY_KEY,
                policyVersion: INTERNAL_POLICY_VERSION,
                locale: resolvedLocale,
            });
            setInternalPolicyAcceptedAt(acceptance.accepted_at);
        } finally {
            setInternalPolicyLoading(false);
        }
    }, [user?.id]);

    useEffect(() => {
        let isActive = true;
        let adminCheckVersion = 0;
        let internalPolicyCheckVersion = 0;

        const checkAdminStatus = async (userId: string) => {
            const currentVersion = ++adminCheckVersion;
            const isAdminUser = await adminIdentityPort.isUserAdmin(userId);

            if (isActive && currentVersion === adminCheckVersion) {
                setIsAdmin(isAdminUser);
            }
        };

        const checkInternalPolicy = async (userId: string) => {
            const currentVersion = ++internalPolicyCheckVersion;
            setInternalPolicyLoading(true);
            try {
                const acceptance = await adminIdentityPort.getLegalAcceptance(INTERNAL_POLICY_KEY, INTERNAL_POLICY_VERSION);
                if (isActive && currentVersion === internalPolicyCheckVersion) {
                    setInternalPolicyAcceptedAt(
                        acceptance?.user_id === userId ? acceptance.accepted_at : null,
                    );
                }
            } catch (error) {
                appLoggerPort.warn("Failed to load internal policy acceptance during session check.", {
                    error,
                    userId,
                });
                if (isActive && currentVersion === internalPolicyCheckVersion) {
                    setInternalPolicyAcceptedAt(null);
                }
            } finally {
                if (isActive && currentVersion === internalPolicyCheckVersion) {
                    setInternalPolicyLoading(false);
                }
            }
        };

        // Initial session check
        adminIdentityPort.getSessionInfo().then(async (sessionInfo) => {
            if (sessionInfo.errorMessage?.includes("Invalid Refresh Token")) {
                await adminIdentityPort.signOut();
                clearSupabaseAuthStorage();
                if (isActive) {
                    setUser(null);
                    setIsAdmin(false);
                    setLoading(false);
                    setInternalPolicyAcceptedAt(null);
                    setInternalPolicyLoading(false);
                }
                return;
            }
            if (!isActive) return;

            const currentUser = sessionInfo.user ?? null;
            if (currentUser) {
                setUser(currentUser);
                setLoading(false);
                checkAdminStatus(currentUser.id);
                checkInternalPolicy(currentUser.id);
                return;
            }

            // Fallback verification can recover transient refresh/session races.
            const verifiedUser = await adminIdentityPort.getCurrentUser();
            if (!isActive) return;

            setUser(verifiedUser);
            setLoading(false);
            if (verifiedUser) {
                checkAdminStatus(verifiedUser.id);
                checkInternalPolicy(verifiedUser.id);
            } else {
                setIsAdmin(false);
                setInternalPolicyAcceptedAt(null);
                setInternalPolicyLoading(false);
            }
        });

        // Listen for auth changes
        const subscription = adminIdentityPort.onAuthStateChange((currentUser) => {
            if (isActive) {
                setUser(currentUser);
                setLoading(false);
                if (currentUser) {
                    checkAdminStatus(currentUser.id);
                    checkInternalPolicy(currentUser.id);
                } else {
                    setIsAdmin(false);
                    setInternalPolicyAcceptedAt(null);
                    setInternalPolicyLoading(false);
                }
            }
        });

        return () => {
            isActive = false;
            subscription.unsubscribe();
        };
    }, []);

    const signOut = async () => {
        await adminIdentityPort.signOut();
    };

    const value = useMemo(() => {
        const platformAuthContext: PlatformAuthContext = {
            user: toPlatformIdentityUser(user),
            sessionId: null,
            isAuthenticated: Boolean(user),
            isAdmin,
            permissions: isAdmin ? ["*"] : [],
        };

        return {
            user,
            isAdmin,
            loading,
            internalPolicyAccepted: Boolean(internalPolicyAcceptedAt),
            internalPolicyAcceptedAt,
            internalPolicyLoading,
            signOut,
            acceptInternalPolicy,
            refreshInternalPolicy,
            platformAuthContext,
        };
    }, [
        user,
        isAdmin,
        loading,
        internalPolicyAcceptedAt,
        internalPolicyLoading,
        acceptInternalPolicy,
        refreshInternalPolicy,
    ]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within AuthProvider");
    }
    return context;
};

export const usePlatformAuthContext = (): PlatformAuthContext => useAuth().platformAuthContext;
